import { createContext, useContext, useState, useEffect } from "react";
import config from "../config";
import { useAuth0 } from "@auth0/auth0-react";

const BASE_URL = config.API.URL;

function ApiProvider(props) {
  let { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  let [jwt, setJwt] = useState(null);
  let [profile, setProfile] = useState({});

  useEffect(() => {
    if (!isAuthenticated) return;
    async function init() {
      let token = await getAccessTokenSilently({
        audience: 'mdb-atlas',
        scope: 'openid profile email'
      });
      setJwt(token);
    }
    init();
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    async function loadUser() {
      let profile = await getUserData();
      // Auto-create a minimal profile on first login so this user's owned
      // URLs/landings resolve to a name instead of "Unknown".
      if (!profile?._id) {
        profile = await ensureProfile();
      }
      setProfile(profile);
    }
    loadUser();
  }, [isAuthenticated]);

  const generateHeaders = async () => {
    let token = jwt ? jwt : await getAccessTokenSilently({
      audience: 'mdb-atlas',
      scope: 'openid profile email'
    });
    if (!jwt) {
      setJwt(token);
    }
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  }

  const deleteRoute = async (id) => {
    const url = `${BASE_URL}/routes/${id}`;
    const result = await fetch(url, {
      method: "DELETE",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return result;
  }

  const fetchLandings = async () => {
    const url = `${BASE_URL}/landings`;
    const landings = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return landings;
  }

  const fetchRoutes = async () => {
    const url = `${BASE_URL}/routes`;
    const routes = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return routes;
  }

  const insertRoute = async (route) => {
    const url = `${BASE_URL}/routes`;
    const result = await fetch(url, {
      method: "POST",
      headers: await generateHeaders(),
      body: JSON.stringify(route)
    }).then(resp => resp.json());
    return result;
  }

  const insertLanding = async (landing, options = {}) => {
    const url = `${BASE_URL}/landings`;
    const body = options.skipShortie ? { ...landing, skipShortie: true } : landing;
    const result = await fetch(url, {
      method: "POST",
      headers: await generateHeaders(),
      body: JSON.stringify(body)
    }).then(resp => resp.json());
    return result;
  }

  const updateRoute = async (id, route) => {
    const url = `${BASE_URL}/routes/${id}`;
    const result = await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(route)
    }).then(resp => resp.json());
    return result;
  }

  const updateLanding = async (id, landing) => {
    const url = `${BASE_URL}/landings/${id}`;
    const result = await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(landing)
    }).then(resp => resp.json());
    return result;
  }

  const getResourceEditors = async (resourceType, resourceId) => {
    const collection = resourceType === "routes" ? "routes" : "landings";
    const url = `${BASE_URL}/${collection}/${encodeURIComponent(resourceId)}/editors`;
    const response = await fetch(url, { method: "GET", headers: await generateHeaders() });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Unable to load editor access.");
    return result;
  }

  const updateResourceEditors = async (resourceType, resourceId, data) => {
    const collection = resourceType === "routes" ? "routes" : "landings";
    const url = `${BASE_URL}/${collection}/${encodeURIComponent(resourceId)}/editors`;
    const response = await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Unable to update editor access.");
    return result;
  }

  const deleteLanding = async (identifier) => {
    const url = `${BASE_URL}/landings/${identifier}`;
    const result = await fetch(url, {
      method: "DELETE",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return result;
  }

  const lookupRoute = async (route) => {
    const url = `${BASE_URL}/routes/lookup?route=${encodeURIComponent(route)}`;
    const resp = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    });
    // The endpoint sends an empty body when no route matches; guard against it.
    const text = await resp.text();
    return text ? JSON.parse(text) : null;
  }

  const getRouteStats = async (route) => {
    const url = `${BASE_URL}/routes/stats?route=${route}`;
    const stats = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return stats;
  }

  const getDashboardFilters = async () => {
    const url = `${BASE_URL}/dashboard/filters`;
    const filters = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return filters;
  }

  const getUserData = async (props) => {
    if (profile._id && !props?.force) {
      return profile;
    }
    const url = `${BASE_URL}/users/me`;
    const user = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    setProfile(user);
    return user;
  }

  const ensureProfile = async () => {
    // Only create when there is genuinely no profile yet and we have an identity.
    if (!user?.sub) {
      return {};
    }
    const url = `${BASE_URL}/users/me`;
    const seed = {
      name: user.name || user.nickname || user.email || user.sub,
      email: user.email
    };
    await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(seed)
    }).then(resp => resp.json());
    return getUserData({ force: true });
  }

  const saveUserData = async (data) => {
    const url = `${BASE_URL}/users/me`;
    const newProfile = {
      _id: data._id,
      name: data.name,
      team: data.team
    };
    const user = await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(newProfile)
    }).then(resp => resp.json());
    setProfile(await getUserData({force: true}));
    return user;
  }

  const getTeams = async () => {
    const url = `${BASE_URL}/users/teams`;
    const teams = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return teams;
  }

  const generatePasskey = async (date) => {
    const url = `${BASE_URL}/passkey?date=${encodeURIComponent(date)}`;
    const result = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return result;
  }

  const getInstruqtTracks = async (environment) => {
    const query = environment ? `?environment=${encodeURIComponent(environment)}` : "";
    const url = `${BASE_URL}/instruqt/tracks${query}`;
    const tracks = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return tracks;
  }

  const getInstruqtManageInvitesUrl = async (environment) => {
    const query = environment ? `?environment=${encodeURIComponent(environment)}` : "";
    const url = `${BASE_URL}/instruqt/manage-invites-url${query}`;
    const result = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return result;
  }

  const createInstruqtInvite = async (payload) => {
    const url = `${BASE_URL}/instruqt/invites`;
    const result = await fetch(url, {
      method: "POST",
      headers: await generateHeaders(),
      body: JSON.stringify(payload)
    }).then(resp => resp.json());
    return result;
  }

  const getAppSettings = async () => {
    const url = `${BASE_URL}/appsettings`;
    const settings = await fetch(url, {
      method: "GET",
      headers: await generateHeaders()
    }).then(resp => resp.json());
    return settings;
  }

  const updateAppSettings = async (data) => {
    const url = `${BASE_URL}/appsettings`;
    const result = await fetch(url, {
      method: "PUT",
      headers: await generateHeaders(),
      body: JSON.stringify(data)
    }).then(resp => resp.json());
    return result;
  }

  let providerState = {
    jwt,
    profile,
    deleteRoute,
    fetchLandings,
    fetchRoutes,
    getDashboardFilters,
    getRouteStats,
    insertRoute,
    insertLanding,
    updateRoute,
    updateLanding,
    getResourceEditors,
    updateResourceEditors,
    deleteLanding,
    getUserData,
    saveUserData,
    getTeams,
    lookupRoute,
    generatePasskey,
    getAppSettings,
    updateAppSettings,
    getInstruqtTracks,
    getInstruqtManageInvitesUrl,
    createInstruqtInvite
  }

  return (
    <ApiContext.Provider value={providerState}>
      {props.children}
    </ApiContext.Provider>
  );
}

const ApiContext = createContext({});
const useApi = () => useContext(ApiContext);

export {
  ApiContext,
  useApi,
  ApiProvider
}
